import React, { useState, useMemo, useEffect } from "react";
import { Modal, Input, DatePicker, Spin, message, Popconfirm } from "antd";
import dayjs from "dayjs";
import MainLayout from "../../layout/MainLayout";
import { Button2, Checkbox, Select, Tag, Button1 } from "../index";
import sortIcon from "../../assets/icons/sort-icon.png";
import LineChart from "../line-chart/LineChart";
import Table from "../table/Table";
import circleIcon from "../../assets/icons/Ellipse 81.png";
import { useAccounts, useRetirementPlans } from "../../data/live";
import { retirementPlansApi } from "../../api/endpoints";

const FREQ_OPTIONS = [
  { name: "Monthly", value: "MONTHLY" },
  { name: "Quarterly", value: "QUARTERLY" },
  { name: "Semiannually", value: "SEMIANNUALLY" },
  { name: "Annually", value: "ANNUALLY" },
];

const last4 = (s) => (s ? String(s).slice(-4) : "0000");

const fmtMoney = (n, ccy) =>
  `${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })} ${ccy || ""}`;

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";

const RetirementPlan = () => {
  const { accounts } = useAccounts();
  const { plans, projection, loading, refresh } = useRetirementPlans();

  const [filterAccountId, setFilterAccountId] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");

  // Add Plan modal
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    accountId: "",
    contributionAmount: "",
    currency: "AUD",
    frequency: "MONTHLY",
    expectedReturnRate: "7.0",
    startDate: null,
    retirementDate: null,
    targetAmount: "",
  });
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    if (accounts.length && !form.accountId) {
      const def = accounts.find((a) => a.isDefault) || accounts[0];
      setForm((f) => ({ ...f, accountId: def.id, currency: def.currency }));
    }
  }, [accounts, form.accountId]);

  // Update currency to match selected wallet
  useEffect(() => {
    const acct = accounts.find((a) => a.id === form.accountId);
    if (acct) setForm((f) => ({ ...f, currency: acct.currency }));
  }, [form.accountId, accounts]);

  const accountOptions = accounts.map((a) => ({
    name: `${a.currency} • ****${last4(a.accountNumber || a.iban)}${
      a.isDefault ? " (Default)" : ""
    }`,
    value: a.id,
  }));

  const filterOptions = [
    { name: "All wallets", value: "" },
    ...accountOptions,
  ];

  // Filter + sort plans for the assumptions table
  const visiblePlans = useMemo(() => {
    let list = projection?.byPlan ? [...projection.byPlan] : [];
    if (filterAccountId) {
      list = list.filter((p) => p.accountId === filterAccountId);
    }
    list.sort((a, b) => {
      let av = a[sortField];
      let bv = b[sortField];
      if (sortField === "contributionAmount" || sortField === "projectedFutureValue") {
        av = Number(av || 0);
        bv = Number(bv || 0);
      } else if (sortField === "startDate" || sortField === "retirementDate") {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      } else {
        av = String(av || "");
        bv = String(bv || "");
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [projection, filterAccountId, sortField, sortDir]);

  const cycleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const chartLabels = projection?.points?.length
    ? projection.points.map((p) => `${p.age} y.o`)
    : [];
  const chartData = projection?.points?.length
    ? projection.points.map((p) => p.value)
    : [];

  const totalAtRetirement = projection?.totalAtRetirement || 0;
  const projectionCurrency = projection?.currency || "AUD";

  const openAddModal = () => {
    setFormError(null);
    setShowModal(true);
  };

  const submitPlan = async () => {
    setFormError(null);
    if (!form.contributionAmount || Number(form.contributionAmount) <= 0) {
      return setFormError("Enter a valid contribution amount");
    }
    if (!form.startDate || !form.retirementDate) {
      return setFormError("Pick both start and retirement dates");
    }
    if (new Date(form.retirementDate) <= new Date(form.startDate)) {
      return setFormError("Retirement date must be after start date");
    }
    setSubmitting(true);
    try {
      await retirementPlansApi.create({
        accountId: form.accountId || null,
        contributionAmount: form.contributionAmount,
        currency: form.currency,
        frequency: form.frequency,
        expectedReturnRate: form.expectedReturnRate,
        startDate: dayjs(form.startDate).format("YYYY-MM-DD"),
        retirementDate: dayjs(form.retirementDate).format("YYYY-MM-DD"),
        targetAmount: form.targetAmount || null,
      });
      message.success("Retirement plan added");
      setShowModal(false);
      setForm((f) => ({
        ...f,
        contributionAmount: "",
        targetAmount: "",
        startDate: null,
        retirementDate: null,
      }));
      refresh();
    } catch (e) {
      setFormError(e.response?.data?.error || e.message || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  const deletePlan = async (id) => {
    try {
      await retirementPlansApi.remove(id);
      message.success("Plan removed");
      refresh();
    } catch (e) {
      message.error(e.response?.data?.error || "Failed to delete");
    }
  };

  const assumptionTableHeader = [
    <Checkbox />,
    <span
      onClick={() => cycleSort("currency")}
      style={{ cursor: "pointer" }}
      title="Sort by account"
    >
      Account {sortField === "currency" ? (sortDir === "asc" ? "↑" : "↓") : ""}
    </span>,
    <span
      onClick={() => cycleSort("contributionAmount")}
      style={{ cursor: "pointer" }}
      title="Sort by amount"
    >
      Amount {sortField === "contributionAmount" ? (sortDir === "asc" ? "↑" : "↓") : ""}
    </span>,
    "Frequency",
    <span
      onClick={() => cycleSort("projectedFutureValue")}
      style={{ cursor: "pointer" }}
      title="Sort by projected value"
    >
      Projected at retirement {sortField === "projectedFutureValue" ? (sortDir === "asc" ? "↑" : "↓") : ""}
    </span>,
    <span
      onClick={() => cycleSort("startDate")}
      style={{ cursor: "pointer" }}
      title="Sort by start date"
    >
      Start Date {sortField === "startDate" ? (sortDir === "asc" ? "↑" : "↓") : ""}
    </span>,
    <span
      onClick={() => cycleSort("retirementDate")}
      style={{ cursor: "pointer" }}
      title="Sort by retirement date"
    >
      Retirement Date {sortField === "retirementDate" ? (sortDir === "asc" ? "↑" : "↓") : ""}
    </span>,
    "Action",
  ];

  const assumptionTableBody = visiblePlans.map((p) => ({
    account: {
      icon: circleIcon,
      name: p.account?.currency || p.currency,
      accNo: `${p.currency}-****${last4(p.account?.accountNumber || p.account?.iban)}`,
    },
    amount: fmtMoney(p.contributionAmount, p.currency),
    invesmentTenor:
      FREQ_OPTIONS.find((f) => f.value === p.frequency)?.name || p.frequency,
    estimatedTargeted: fmtMoney(p.projectedFutureValue, p.currency),
    startDate: formatDate(p.startDate),
    retirementDate: formatDate(p.retirementDate),
    _delete: (
      <Popconfirm
        title="Remove this plan?"
        onConfirm={() => deletePlan(p.id)}
        okText="Yes"
        cancelText="No"
      >
        <span style={{ color: "#FC555B", cursor: "pointer", fontSize: 12, fontWeight: 500 }}>
          Delete
        </span>
      </Popconfirm>
    ),
  }));

  return (
    <>
      <MainLayout NavName="Plan For Retirement" expand={false}>
        <div>
          <div className="container mt-5">
            <div className="row gap-3">
              <div className="d-flex justify-content-md-between justify-content-center gap-md-0 gap-3 align-items-center flex-wrap flex-md-nowrap">
                <span className="fs-5 fw-semibold">Retirement Savings</span>
                <Select
                  value={filterAccountId}
                  defaultValue={accounts.length ? "All wallets" : "Loading wallets..."}
                  className="bg-light rounded-5 w-auto"
                  popupClassName="bg-White"
                  options={filterOptions}
                  onChange={(e) => setFilterAccountId(e.target.value)}
                />
                <Button1
                  buttonName={"+ Add Plan"}
                  className={"btn-dark"}
                  onClick={openAddModal}
                />
              </div>

              <div className="col-12">
                <div className="d-flex flex-column gap-3 border rounded-3">
                  <div className="d-flex justify-content-between p-3 px-4 align-items-center border-bottom flex-wrap flex-md-nowrap gap-md-0 gap-3">
                    <div className="d-flex flex-column gap-1">
                      <span
                        className="fs14px fw-medium"
                        style={{ color: "#7D7D7D" }}
                      >
                        Estimated retirement savings
                      </span>
                      <span
                        className="fs-4 fw-semibold"
                        style={{ color: "#1bbb6b" }}
                      >
                        {fmtMoney(totalAtRetirement, projectionCurrency)}
                      </span>
                    </div>

                    {plans.length === 0 && !loading ? (
                      <Tag
                        tagName={
                          "No retirement plans yet, click \"Add Plan\" to project your savings."
                        }
                        className={
                          "p-2 px-4 rounded-2 fs12px fw-medium tag-box-width-control"
                        }
                        tagStyle={{
                          backgroundColor: "#FEF5EB",
                          color: "#F7A64F",
                        }}
                      />
                    ) : (
                      <Tag
                        tagName={
                          `Live projection from ${plans.length} plan${plans.length === 1 ? "" : "s"}. Edit any plan to update.`
                        }
                        className={
                          "p-2 px-4 rounded-2 fs12px fw-medium tag-box-width-control"
                        }
                        tagStyle={{
                          backgroundColor: "rgba(27, 187, 107, 0.1)",
                          color: "#1bbb6b",
                        }}
                      />
                    )}
                  </div>

                  <div className="p-3 px-4">
                    {loading ? (
                      <div className="d-flex justify-content-center py-5">
                        <Spin size="large" />
                      </div>
                    ) : chartData.length === 0 ? (
                      <div
                        className="text-center py-5"
                        style={{ color: "var(--text-muted)" }}
                      >
                        Add at least one plan to see your projection.
                      </div>
                    ) : (
                      <LineChart
                        labels={chartLabels}
                        className="border rounded-3"
                        data={chartData}
                        borderColor={"#1bbb6b"}
                        fillColor="rgba(76, 167, 112, 0.4)"
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="col-12">
                <div className="d-flex flex-column gap-3 border rounded-3">
                  <div className="d-flex justify-content-between align-items-center p-3 px-4 border-bottom">
                    <span className="fs-5 fw-medium">Assumptions</span>
                    <Button2
                      buttonName={`Sort: ${sortField} (${sortDir})`}
                      className={"border"}
                      divClassName={"gap-2"}
                      icon={<img src={sortIcon} alt="sort" />}
                      onClick={() =>
                        setSortDir((d) => (d === "asc" ? "desc" : "asc"))
                      }
                    />
                  </div>

                  <div className="p-3 px-4">
                    {loading ? (
                      <div className="d-flex justify-content-center py-4">
                        <Spin />
                      </div>
                    ) : assumptionTableBody.length === 0 ? (
                      <div
                        className="text-center py-4"
                        style={{ color: "var(--text-muted)" }}
                      >
                        {filterAccountId
                          ? "No plans for this wallet."
                          : "No retirement plans yet."}
                      </div>
                    ) : (
                      <Table
                        AssumptionTable={true}
                        tableHeaders={assumptionTableHeader}
                        tableBody={assumptionTableBody}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>

      <Modal
        title="Add Retirement Plan"
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
        destroyOnClose
      >
        <div className="d-flex flex-column gap-3">
          <div className="d-flex flex-column gap-1">
            <label className="fs12px">Wallet</label>
            <Select
              value={form.accountId}
              defaultValue="Select wallet"
              options={accountOptions}
              className="p-2"
              onChange={(e) => setForm((f) => ({ ...f, accountId: e.target.value }))}
            />
          </div>

          <div className="d-flex gap-2">
            <div className="d-flex flex-column gap-1 w-100">
              <label className="fs12px">Contribution amount</label>
              <Input
                placeholder="500"
                value={form.contributionAmount}
                onChange={(e) =>
                  setForm((f) => ({ ...f, contributionAmount: e.target.value }))
                }
                className="p-2"
              />
            </div>
            <div className="d-flex flex-column gap-1" style={{ minWidth: 100 }}>
              <label className="fs12px">Currency</label>
              <Input value={form.currency} disabled className="p-2" />
            </div>
          </div>

          <div className="d-flex gap-2">
            <div className="d-flex flex-column gap-1 w-100">
              <label className="fs12px">Frequency</label>
              <Select
                value={form.frequency}
                defaultValue="Monthly"
                options={FREQ_OPTIONS}
                className="p-2"
                onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))}
              />
            </div>
            <div className="d-flex flex-column gap-1 w-100">
              <label className="fs12px">Expected return %/yr</label>
              <Input
                placeholder="7.0"
                value={form.expectedReturnRate}
                onChange={(e) =>
                  setForm((f) => ({ ...f, expectedReturnRate: e.target.value }))
                }
                className="p-2"
              />
            </div>
          </div>

          <div className="d-flex gap-2">
            <div className="d-flex flex-column gap-1 w-100">
              <label className="fs12px">Start date</label>
              <DatePicker
                value={form.startDate ? dayjs(form.startDate) : null}
                onChange={(d) => setForm((f) => ({ ...f, startDate: d?.toISOString() || null }))}
                className="w-100 p-2"
              />
            </div>
            <div className="d-flex flex-column gap-1 w-100">
              <label className="fs12px">Retirement date</label>
              <DatePicker
                value={form.retirementDate ? dayjs(form.retirementDate) : null}
                onChange={(d) =>
                  setForm((f) => ({ ...f, retirementDate: d?.toISOString() || null }))
                }
                className="w-100 p-2"
              />
            </div>
          </div>

          <div className="d-flex flex-column gap-1">
            <label className="fs12px">Target amount (optional)</label>
            <Input
              placeholder="1,000,000"
              value={form.targetAmount}
              onChange={(e) =>
                setForm((f) => ({ ...f, targetAmount: e.target.value }))
              }
              className="p-2"
            />
          </div>

          {formError && (
            <span className="fs12px" style={{ color: "#FC555B" }}>
              {formError}
            </span>
          )}

          <div className="d-flex justify-content-end gap-2 mt-2">
            <Button1
              buttonName="Cancel"
              className="btn-outline-secondary"
              onClick={() => setShowModal(false)}
              disabled={submitting}
            />
            <Button1
              buttonName={submitting ? "Saving..." : "Add Plan"}
              className="btn-dark"
              onClick={submitPlan}
              disabled={submitting}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default RetirementPlan;
